import { useEffect, useRef, useState } from 'react';
import { httpsCallable, FunctionsError } from 'firebase/functions';
import { orderBy, limit } from 'firebase/firestore';
import type {
  BotDoc, ChatMessageDoc, ConsequenceChatMessage, SceneIntroChatMessage,
} from '@spirits/shared';
import { useCollection } from '../hooks/useCollection';
import { ActionPanel } from '../scene/ActionPanel';
import { GenerateNextButton } from '../play/GenerateNextButton';
import { functions } from '../firebase';

const whisperToCallable = httpsCallable<{ botId: string; text: string }, { ok: boolean }>(
  functions,
  'whisperTo',
);
const suggestWhispersCallable = httpsCallable<{ botId: string }, { ok: boolean; suggestions: string[] }>(
  functions,
  'suggestWhispers',
);

interface Props {
  bot: BotDoc & { id: string };
  locked?: boolean;
  blockedBy?: (BotDoc & { id: string }) | null;
  nextBot?: (BotDoc & { id: string }) | null;
  onSelectNext?: (id: string) => void;
  isCurrent?: boolean;
}

export function ChatPanel({
  bot,
  locked = false,
  blockedBy = null,
  nextBot = null,
  onSelectNext,
  isCurrent = true,
}: Props) {
  return (
    <ChatBody
      bot={bot}
      locked={locked}
      blockedBy={blockedBy}
      nextBot={nextBot}
      onSelectNext={onSelectNext}
      isCurrent={isCurrent}
    />
  );
}

function NextBotButton({
  nextBot,
  onSelectNext,
}: {
  nextBot: (BotDoc & { id: string }) | null;
  onSelectNext?: (id: string) => void;
}) {
  if (!nextBot || !onSelectNext) return null;
  return (
    <button
      type="button"
      onClick={() => onSelectNext(nextBot.id)}
      className="mt-2 mx-auto block rounded-lg border border-amber-400/50 bg-amber-950/30 text-amber-100 hover:bg-amber-900/40 px-4 py-1.5 text-sm cursor-pointer"
    >
      Guide {nextBot.displayName} →
    </button>
  );
}

function ChatBody({
  bot,
  locked,
  blockedBy,
  nextBot,
  onSelectNext,
  isCurrent,
}: {
  bot: BotDoc & { id: string };
  locked: boolean;
  blockedBy: (BotDoc & { id: string }) | null;
  nextBot: (BotDoc & { id: string }) | null;
  onSelectNext?: (id: string) => void;
  isCurrent: boolean;
}) {
  const { data: messages, loading } = useCollection<ChatMessageDoc>(
    `bots/${bot.id}/chat`,
    orderBy('at', 'desc'),
    limit(40),
  );
  const ordered = [...messages].reverse();
  const decided = bot.decisionState === 'decided';
  const situation = bot.decision?.situation ?? null;
  const callToAction = bot.decision?.callToAction ?? null;

  // A scene transition writes a `scene-intro` card. The opening scene has none
  // (cold start), so we pin its situation at the top as the character's standing
  // thought. Once an inline scene-intro card exists for the current situation,
  // we let THAT card carry it in chronological order instead.
  const introInStream =
    !!situation && ordered.some((m) => m.kind === 'scene-intro' && m.situation === situation);
  const pinSituation = !!situation && !introInStream;
  const noConversationYet = ordered.length === 0;

  const scrollRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
  }, [ordered.length]);

  return (
    <>
      <div ref={scrollRef} className="flex-1 overflow-y-auto px-5 py-4 space-y-3 min-h-0">
        {loading && ordered.length === 0 && <p className="text-slate-500 text-sm">listening…</p>}

        {pinSituation && (
          <div className="space-y-2">
            <span className="text-[10px] uppercase tracking-widest text-slate-600">
              {bot.displayName} · to themselves
            </span>
            <p className="text-[15px] leading-relaxed text-slate-300 whitespace-pre-line">
              <span className="text-amber-400/70">“</span>
              {situation}
              <span className="text-amber-400/70">”</span>
            </p>
            {callToAction && !decided && (
              <div className="rounded-lg border border-sky-500/25 bg-sky-950/20 px-3 py-2 not-italic mt-6">
                <p className="text-sm leading-snug text-sky-100/80">{callToAction}</p>
              </div>
            )}
          </div>
        )}

        {!loading && !situation && noConversationYet && (
          <p className="text-slate-500 text-sm">They haven't heard from anyone yet. Pick a road, or guide them with your voice.</p>
        )}

        {ordered.map((m) => (
          <ChatBubble key={m.id} bot={bot} msg={m} />
        ))}
        {!decided && ordered.length > 0 && ordered[ordered.length - 1].kind === 'player' && (
          <ThinkingBubble bot={bot} />
        )}
      </div>

      {locked && blockedBy ? (
        <div className="flex-none border-t border-slate-800 px-4 py-3 bg-slate-900/40">
          <p className="text-xs text-amber-200/80 text-center leading-snug">
            <span className="text-amber-400/70">✦ </span>
            {blockedBy.displayName} decides first — guide them before {bot.displayName} can move.
          </p>
          <NextBotButton nextBot={nextBot} onSelectNext={onSelectNext} />
        </div>
      ) : !isCurrent ? (
        <div className="flex-none border-t border-slate-800 px-4 py-3 text-center text-xs text-slate-500">
          An earlier scene — {bot.displayName}'s part of the story is settled.
        </div>
      ) : decided ? (
        <div className="flex-none border-t border-slate-800 px-4 py-3 text-center">
          <p className="text-xs text-emerald-300/80">
            {bot.displayName} has chosen their road. {nextBot ? 'Guide the next one.' : 'Everyone has decided.'}
          </p>
          {nextBot ? (
            <NextBotButton nextBot={nextBot} onSelectNext={onSelectNext} />
          ) : (
            <div className="mt-2">
              <GenerateNextButton storyId={bot.storyId} />
            </div>
          )}
        </div>
      ) : (
        <>
          <ActionPanel bot={bot} locked={locked} />
          <WhisperBox bot={bot} disabled={false} />
        </>
      )}
    </>
  );
}

function ChatBubble({ bot, msg }: { bot: BotDoc & { id: string }; msg: ChatMessageDoc & { id: string } }) {
  if (msg.kind === 'player') {
    return (
      <div className="flex flex-col items-end">
        <span className="text-[10px] uppercase tracking-widest text-slate-600 mb-0.5">{msg.playerName}</span>
        <div className="max-w-[85%] rounded-2xl rounded-br-sm bg-amber-900/30 border border-amber-900/40 px-3 py-2 text-sm text-amber-100/90">
          {msg.text}
        </div>
      </div>
    );
  }
  if (msg.kind === 'bot') {
    return (
      <div className="flex flex-col items-start">
        <span className="text-[10px] uppercase tracking-widest text-slate-600 mb-0.5">{bot.displayName}</span>
        <div className="max-w-[85%] rounded-2xl rounded-bl-sm bg-slate-800/70 border border-slate-700/60 px-3 py-2 text-sm text-slate-200">
          {msg.text}
        </div>
      </div>
    );
  }
  if (msg.kind === 'action') {
    return (
      <div className="flex justify-center">
        <div className="w-full rounded-xl border border-amber-500/30 bg-amber-950/15 px-3 py-2 text-sm">
          <span className="text-[10px] uppercase tracking-widest text-amber-300/80">✦ {bot.displayName} acts</span>
          <p className="text-amber-100/90 leading-snug mt-1">“{msg.description}”</p>
        </div>
      </div>
    );
  }
  if (msg.kind === 'consequence') {
    return <ConsequenceBubble msg={msg} />;
  }
  if (msg.kind === 'scene-intro') {
    return <SceneIntroBubble msg={msg} />;
  }
  return null;
}

function SceneIntroBubble({ msg }: { msg: SceneIntroChatMessage & { id: string } }) {
  return (
    <div className="py-2">
      <div className="flex items-center gap-3">
        <span className="h-px flex-1 bg-gradient-to-r from-transparent to-amber-500/30" />
        <span className="text-[10px] uppercase tracking-[0.2em] text-amber-300/80 whitespace-nowrap">
          ✦ {msg.sceneName}
        </span>
        <span className="h-px flex-1 bg-gradient-to-l from-transparent to-amber-500/30" />
      </div>
      <p className="mt-2 text-center text-sm leading-snug text-slate-300 px-2 whitespace-pre-line">
        <span className="text-amber-400/70">“</span>
        {msg.situation}
        <span className="text-amber-400/70">”</span>
      </p>
    </div>
  );
}

function ConsequenceBubble({ msg }: { msg: ConsequenceChatMessage & { id: string } }) {
  return (
    <div className="flex justify-center">
      <div className="w-full rounded-xl border border-slate-600/40 bg-slate-800/30 px-3 py-2.5 space-y-1.5">
        <span className="text-[10px] uppercase tracking-widest text-slate-400">↳ what happened</span>
        <p className="text-sm leading-snug text-slate-300">{msg.consequence}</p>
      </div>
    </div>
  );
}

function ThinkingBubble({ bot }: { bot: BotDoc & { id: string } }) {
  return (
    <div className="flex flex-col items-start">
      <span className="text-[10px] uppercase tracking-widest text-slate-600 mb-0.5">{bot.displayName}</span>
      <div className="rounded-2xl rounded-bl-sm bg-slate-800/70 border border-slate-700/60 px-3 py-2.5">
        <span className="flex gap-1" aria-label="considering">
          <span className="w-1.5 h-1.5 rounded-full bg-slate-400 animate-bounce [animation-delay:-0.3s]" />
          <span className="w-1.5 h-1.5 rounded-full bg-slate-400 animate-bounce [animation-delay:-0.15s]" />
          <span className="w-1.5 h-1.5 rounded-full bg-slate-400 animate-bounce" />
        </span>
      </div>
    </div>
  );
}

function WhisperBox({ bot, disabled }: { bot: BotDoc & { id: string }; disabled: boolean }) {
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const [suggesting, setSuggesting] = useState(false);
  const [suggestions, setSuggestions] = useState<string[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (!sending && !disabled) textareaRef.current?.focus();
  }, [sending, disabled]);

  async function suggest() {
    if (suggesting || disabled) return;
    setSuggesting(true);
    setError(null);
    try {
      const res = await suggestWhispersCallable({ botId: bot.id });
      setSuggestions(res.data.suggestions ?? []);
    } catch (e) {
      setError((e as FunctionsError)?.message ?? 'no words come');
    } finally {
      setSuggesting(false);
    }
  }

  function pickSuggestion(s: string) {
    setText(s);
    setSuggestions(null);
    textareaRef.current?.focus();
  }

  async function send() {
    const trimmed = text.trim();
    if (!trimmed || sending || disabled) return;
    setSending(true);
    setError(null);
    setSuggestions(null);
    try {
      await whisperToCallable({ botId: bot.id, text: trimmed });
      setText('');
    } catch (e) {
      setError((e as FunctionsError)?.message ?? 'the voice falters');
    } finally {
      setSending(false);
    }
  }

  return (
    <form className="relative border-t border-slate-800 p-4 space-y-2" onSubmit={(e) => { e.preventDefault(); send(); }}>
      <div className="flex items-center justify-between">
        <span className="text-[10px] uppercase tracking-widest text-sky-300/70">Guide with your voice</span>
        <button
          type="button"
          onClick={suggest}
          disabled={suggesting || disabled}
          className="text-[11px] flex items-center gap-1 rounded-md border border-sky-500/40 bg-sky-950/30 text-sky-200/90 hover:bg-sky-900/40 px-2 py-1 cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed"
          title="Get a few things you could say, then edit before sending"
        >
          <span className="leading-none">✎</span>
          {suggesting ? '…' : 'Suggest'}
        </button>
      </div>

      {suggestions && (
        <div className="absolute bottom-full left-0 right-0 mx-4 mb-2 rounded-lg border border-amber-500/30 bg-slate-900/95 backdrop-blur p-2 shadow-xl space-y-1.5 z-10">
          <div className="flex items-center justify-between px-1">
            <span className="text-[10px] uppercase tracking-widest text-sky-300/70">＝ tap one to use, then edit</span>
            <button type="button" onClick={() => setSuggestions(null)} className="text-slate-500 hover:text-slate-300 text-xs leading-none cursor-pointer" aria-label="dismiss suggestions">✕</button>
          </div>
          {suggestions.length === 0 ? (
            <p className="text-xs text-slate-500 px-1 py-1">no suggestions came — use your own words.</p>
          ) : (
            suggestions.map((s, i) => (
              <button
                key={i}
                type="button"
                onClick={() => pickSuggestion(s)}
                className="w-full text-left rounded-md border border-slate-700/60 bg-slate-800/60 hover:bg-slate-700/60 hover:border-amber-400/40 px-3 py-2 text-sm text-slate-200 cursor-pointer"
              >
                {s}
              </button>
            ))
          )}
        </div>
      )}

      {error && <p className="text-xs text-rose-400">{error}</p>}
      <div className="flex gap-2">
        <textarea
          ref={textareaRef}
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
          disabled={sending || disabled}
          rows={2}
          placeholder={`whisper to ${bot.displayName}…`}
          maxLength={500}
          className="flex-1 rounded-md bg-slate-950 border border-slate-800 px-3 py-2 text-sm text-slate-100 placeholder:text-slate-600 focus:outline-none focus:border-sky-400/60 resize-none"
        />
        <button
          type="submit"
          disabled={sending || disabled || !text.trim()}
          className="rounded-md bg-sky-500/80 hover:bg-sky-400 text-slate-950 font-medium px-4 py-2 text-sm disabled:opacity-30 disabled:cursor-not-allowed cursor-pointer"
        >
          {sending ? '…' : 'Guide'}
        </button>
      </div>
    </form>
  );
}
